import Add16 from '@carbon/icons-react/es/add/16';
import { PDFExport, PDFExportProps } from '@progress/kendo-react-pdf';
import { useBoolean } from 'ahooks';
import { Col, Dropdown, Row } from 'antd';
import { difference } from 'lodash';
import moment from 'moment';
import React, { FC, LegacyRef, useCallback, useEffect, useState } from 'react';
import { browserName } from 'react-device-detect';
import { FullScreenHandle } from 'react-full-screen';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import { ProjectId } from 'api/models';
import { ProjectSource } from 'api/project';
import { FieldValueType, StatusGroup } from 'api/tasks';
import ProjectPlanGBG from 'assets/plan-bg.png';
import { Menu } from 'components/atoms/dropdown';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { PermissionRuleName } from 'constant';
import { IconTypeContext } from 'contexts/icon-type';
import { useProjectPhaseStatusField } from 'data/fields/hooks';
import { ProjectPlanFilter, useProjectDetail } from 'data/projects';
import { useCurrentUserProjectAccess } from 'hooks/permissions';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useAccount } from 'hooks/use-account';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslation } from 'hooks/use-translation';
import { useProjectWebsocket } from 'hooks/websockets';
import { ImportFromTemplate } from './import-from-template';
import { PDFInfo } from './pdf-info';
import { PDFLegend } from './pdf-legend';
import { ProjectPhaseModal } from './project-phase-modal';
import { MinimizedSwimlane, Swimlane, useSwimlaneHeight } from './swimlane';

enum BrowserNames {
  Safari = 'Safari',
}

const RowWrapper = styled(Row)<{
  showBackground?: boolean;
}>`
  position: relative;
  flex: 1;

  &:before {
    background-color: var(--coolest-gray-10);
    content: '';
    position: absolute;
    top: 79px;
    left: calc(-1 * var(--layout-01));
    right: 0;
    bottom: 0;
    background: rgb(249, 249, 250);
    ${ifProp(
      prop('showBackground', true),
      css`
        background: repeat-x center
            linear-gradient(
              184deg,
              rgba(249, 249, 250, 1) 0%,
              rgba(249, 249, 250, 1) 40%,
              rgba(255, 255, 255, 0.8) 100%
            ),
          url(${ProjectPlanGBG});

        background-size: 200px;
      `,
    )};
  }
`;

const SwimlaneWrapper = styled(Col)<{
  elementHeight: number;
}>`
  display: flex;
  overflow-x: auto;
  width: 100%;
  height: ${(props) => `calc(100vh - ${props.elementHeight}px)`};
`;

const CreatePhaseWrapper = styled.div`
  align-items: center;
  background: var(--white-100);
  display: flex;
  height: 80px;
  padding: 0 var(--layout-04);
`;

const ProjectPlanWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ExportWrapper = styled.div.attrs({ id: 'print-project-plan' })<{
  isLandscape: boolean;
  maxTaskCount: number;
}>`
  width: ${(props) =>
    props.isLandscape ? `${props.maxTaskCount * 10}rem` : 'fit-content'};
  min-width: 100%;
`;

const ExportSwimlaneContainer = styled.div`
  display: flex;
`;

const ExportSwimlaneWrapper = styled.div`
  margin: var(--spacing-04);
`;

interface ProjectPlanListViewProps {
  filter: ProjectPlanFilter;
  handle: FullScreenHandle;
  projectId: number;
  projectPhaseIds: number[];
  showPrivateTasks?: boolean;
  sourceTemplates?: ProjectSource[];
  pdfExportRef: LegacyRef<PDFExport>;
  isExportUI: boolean;
  kendoSettings: AtLeast<PDFExportProps, 'landscape'>;
}

export const ProjectPlanListView: FC<ProjectPlanListViewProps> = ({
  filter,
  handle,
  projectId,
  projectPhaseIds,
  showPrivateTasks,
  sourceTemplates,
  pdfExportRef,
  isExportUI,
  kendoSettings,
}) => {
  const createProjectPhaseText = useTranslation('projects.phase.create');
  const [showCreatePhasePopup, toggleShowCreatePhasePopup] = useState(false);
  const [
    isImportVisible,
    { setFalse: closeDrawer, setTrue: openImportDrawer },
  ] = useBoolean(false);
  const { isProjectAdmin } = useCurrentUserProjectAccess(ProjectId(projectId));
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasPhaseCreateAccess =
    isProjectAdmin ||
    isAllowedForCurrentUser(PermissionRuleName.ProjectPhaseCreateAction);

  useProjectWebsocket(ProjectId(projectId));

  const {
    metaData: { showProjectPlanBackground = true },
  } = useAccount();

  const { data } = useProjectDetail(ProjectId(projectId));

  const tasksCount: number[] = data
    ? data.projectPhases.map((phase) => phase.totalTaskCount ?? 0)
    : [0];
  const maxTasksCount = Math.max(...tasksCount);

  /*
  creating a mapped object of phaseId's and their statuses to
  decide whether the phase has to be minimized by default or not
  */
  const statusField = useProjectPhaseStatusField();
  const phaseStatusMapping = data?.projectPhases.reduce<
    Record<number, FieldValueType>
  >((acc, curr) => {
    const status = curr.fields.find((f) => f.fieldId === statusField.fieldId);

    if (status) {
      acc[curr.projectPhaseId] = status.fieldValue;
    }

    return acc;
  }, {});

  const [localStorage, setLocalStorage] = useLocalStorage(
    'minimizedPhaseView',
    {},
  );

  const calculateSwimlaneState = useCallback(
    () =>
      projectPhaseIds.reduce((acc, curr) => {
        if (localStorage[curr]) {
          acc[curr] = localStorage[curr];

          return acc;
        }

        acc[curr] = phaseStatusMapping?.[curr] === StatusGroup.Completed;

        return acc;
      }, {}),
    [localStorage, phaseStatusMapping, projectPhaseIds],
  );

  const [swimlaneState, setSwimlaneState] = useState(calculateSwimlaneState);

  useEffect(() => {
    const newState = calculateSwimlaneState();

    if (
      difference(Object.keys(newState), Object.keys(swimlaneState)).length > 0
    ) {
      setSwimlaneState(newState);
    }
  }, [calculateSwimlaneState, swimlaneState]);

  useEffect(() => {
    setLocalStorage(swimlaneState);
  }, [swimlaneState, setLocalStorage]);
  const { height, ref } = useSwimlaneHeight();

  const fileName = `${data?.projectName.split(' ').join('_')}_${moment().format(
    'DDMMMYY_HH_mm',
  )}`;

  return (
    <>
      <ProjectPlanWrapper>
        <RowWrapper
          showBackground={
            showProjectPlanBackground && browserName !== BrowserNames.Safari
          }
        >
          <SwimlaneWrapper ref={ref} elementHeight={height}>
            {projectPhaseIds.map((phaseId, index) => (
              <>
                <MinimizedSwimlane
                  phaseId={Number(phaseId)}
                  projectId={projectId}
                  filter={filter}
                  setSwimlaneState={setSwimlaneState}
                  swimlaneState={swimlaneState}
                  isClose={handle.active ? true : !swimlaneState[phaseId]}
                  index={index}
                />
                <Swimlane
                  key={phaseId}
                  phaseId={Number(phaseId)}
                  projectId={projectId}
                  filter={filter}
                  isFullScreen={handle.active}
                  showPrivateTasks={showPrivateTasks}
                  projectPhaseIds={projectPhaseIds}
                  setSwimlaneState={
                    handle.active ? undefined : setSwimlaneState
                  }
                  swimlaneState={handle.active ? undefined : swimlaneState}
                  isClose={handle.active ? false : swimlaneState[phaseId]}
                  sourceTemplates={sourceTemplates}
                />
              </>
            ))}
            {!handle.active && hasPhaseCreateAccess && (
              <CreatePhaseWrapper className="create-phase-icon">
                <Dropdown
                  trigger={['hover']}
                  overlay={
                    <Menu>
                      <Menu.Item key="import" onClick={openImportDrawer}>
                        <FormattedMessage id="projects.phase.import" />
                      </Menu.Item>
                      <Menu.Item
                        key="create"
                        onClick={() => toggleShowCreatePhasePopup(true)}
                      >
                        {createProjectPhaseText}
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <TertiaryButton
                    icon={<Add16 />}
                    type="default"
                    size="small"
                    data-test-id="add_phase_button_inside_phase"
                  />
                </Dropdown>
                <ProjectPhaseModal
                  visible={showCreatePhasePopup}
                  projectId={ProjectId(projectId)}
                  onClose={() => toggleShowCreatePhasePopup(false)}
                />
                <ImportFromTemplate
                  projectId={ProjectId(projectId)}
                  visible={isImportVisible}
                  onClose={closeDrawer}
                />
              </CreatePhaseWrapper>
            )}
          </SwimlaneWrapper>
        </RowWrapper>
      </ProjectPlanWrapper>
      {isExportUI && (
        <PDFExport
          ref={pdfExportRef}
          fileName={fileName}
          margin="2cm"
          landscape={kendoSettings.landscape}
        >
          <ExportWrapper
            isLandscape={kendoSettings.landscape || false}
            maxTaskCount={maxTasksCount}
          >
            <IconTypeContext.Provider value={{ isExportUI }}>
              <div className="pdf-extra">
                <PDFInfo projectId={ProjectId(projectId)} filter={filter} />
                <PDFLegend />
              </div>

              <ExportSwimlaneContainer>
                {projectPhaseIds.map((phaseId) => (
                  <ExportSwimlaneWrapper>
                    <Swimlane
                      key={phaseId}
                      phaseId={Number(phaseId)}
                      projectId={projectId}
                      filter={filter}
                      isFullScreen={handle.active}
                      showPrivateTasks={showPrivateTasks}
                      projectPhaseIds={projectPhaseIds}
                      setSwimlaneState={
                        handle.active ? undefined : setSwimlaneState
                      }
                      swimlaneState={handle.active ? undefined : swimlaneState}
                      isClose={false}
                      sourceTemplates={sourceTemplates}
                    />
                  </ExportSwimlaneWrapper>
                ))}
              </ExportSwimlaneContainer>
            </IconTypeContext.Provider>
          </ExportWrapper>
        </PDFExport>
      )}
    </>
  );
};
